import { API_URL } from './config.js';
import { ApiError, apiFetch } from './api.js';
import type { CreditSummary } from '../session/types.js';
import type { Deliverable, Order, OrderStatus, Site, TimelineEvent } from './ordersApi.js';
import type { LedgerEntry, PaymentRecord } from './creditsApi.js';

export interface AdminOrderListItem extends Order {
  businessName: string;
  agencyName: string;
}

export interface AdminOrderDetail {
  order: Order;
  timeline: TimelineEvent[];
  deliverables: Deliverable[];
  /** The delivered site and its handover - Item 9.4. Null until it is live. */
  site: Site | null;
}

/** The four account states - D-14 ruling 1. */
export type AgencyStatus = 'invited' | 'active' | 'suspended' | 'closed';

/** Where an agency has got to - D-14 Item 6. Derived by the server, never stored. */
export type Lifecycle = 'invited' | 'signed_up' | 'funded' | 'active' | 'dormant';

export interface AdminAgencyListItem {
  id: string;
  name: string;
  billingEmail: string;
  country: string;
  status: AgencyStatus;
  statusNote: string | null;
  statusChangedAt: string | null;
  cohort: string | null;
  userCount: number;
  createdAt: string;
  /* ---------------------------------------------------- D-14 Item 6 ---- */
  /** Greatest of the last session, the last order and the last credited payment. */
  lastActiveAt: string | null;
  /** The balance minus what open orders have reserved. The server subtracts. */
  availableCredits: number;
  heldCredits: number;
  orderCount: number;
  lastOrderAt: string | null;
  /** Credited goods plus tax, minus what came back out. Minor units. */
  lifetimePaidMinor: number;
  /** Null for a suspended or closed agency: they show their state instead. */
  lifecycle: Lifecycle | null;
  /** The address they sign in with - WAIT-B defect 18. Not the billing email. */
  ownerEmail: string | null;
  /* ---------------------------------------------------------- D-16 ------ */
  businessWebsite: string | null;
  businessConfirmedAt: string | null;
  taxIdStatus: 'none' | 'unchecked' | 'valid' | 'invalid' | 'unavailable';
  /** Any payment on this account the webhook cross-check flagged - item 8. */
  taxFlagged: boolean;
}

export interface AdminAction {
  id: string;
  action: string;
  subjectType: string;
  subjectId: string;
  reason: string | null;
  details: Record<string, unknown>;
  actor: string;
  createdAt: string;
}

/** One invite, as the agency page lists them. The history is the rows. */
export interface AgencyInvite {
  id: string;
  email: string;
  expiresAt: string;
  acceptedAt: string | null;
  invalidatedAt: string | null;
  sentAt: string;
  /** Derived by the server, so the page never re-decides what "expired" means. */
  state: 'open' | 'accepted' | 'invalidated' | 'expired';
}

export interface AdminAgencyDetail {
  agency: {
    id: string;
    name: string;
    billingEmail: string;
    country: string;
    vatNumber: string | null;
    status: AgencyStatus;
    /** Internal. Operator-only, and never served to the agency itself. */
    statusReason: string | null;
    /** What the customer reads on their suspended banner. */
    statusNote: string | null;
    statusChangedAt: string | null;
    cohort: string | null;
    /** D-16 item 10. Everything the operator needs about the tax position. */
    businessWebsite: string | null;
    businessConfirmedAt: string | null;
    taxIdStatus: 'none' | 'unchecked' | 'valid' | 'invalid' | 'unavailable';
    taxIdCheckedAt: string | null;
    businessConfirmedVersion: string | null;
    /** VIES's own reply, where it gave one: the trader name and address. */
    taxIdCheckDetail: { name?: string | null; address?: string | null; note?: string } | null;
    createdAt: string;
  };
  credits: CreditSummary;
  ledger: { entries: LedgerEntry[]; total: number };
  payments: PaymentRecord[];
  sites: Site[];
  adminActions: AdminAction[];
  invites: AgencyInvite[];
  /** How many activity rows there are. The timeline itself is its own route. */
  activityTotal: number;
  /**
   * The owner's login details - WAIT-B defect 18.
   *
   * `previousEmail` is null until an address has moved, so the "Previously ..."
   * line appears exactly when there is something to say.
   */
  owner: {
    email: string;
    name: string;
    previousEmail: string | null;
    previousEmailChangedAt: string | null;
    /** C-37. Whether the owner has confirmed this login address. */
    emailVerified: boolean;
  } | null;
  /** Phase C. The GoHighLevel line. Absent only from a server older than Phase C. */
  ghl?: GhlAgencyStatus;
  /** C-132. Meta reporting, and whether this agency is kept out of it. */
  meta?: MetaAgencyStatus;
}

/* ------------------------------------------- GoHighLevel sync (Phase C) --- */

export interface GhlAgencyStatus {
  /** False when the server has no GoHighLevel settings. */
  enabled: boolean;
  state: 'kept_out' | 'error' | 'synced' | 'not_synced';
  excluded: boolean;
  contactId: string | null;
  syncedAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
}

export interface GhlSyncRun {
  trigger: 'schedule' | 'operator' | 'command_line';
  startedAt: string;
  finishedAt: string | null;
  total: number;
  done: number;
  sent: number;
  skipped: number;
  keptOut: number;
  failed: number;
}

export interface GhlSyncAllView {
  enabled: boolean;
  running: GhlSyncRun | null;
  last: GhlSyncRun | null;
  /** A pressed run the server restarted in the middle of. */
  interrupted: { startedAt: string; total: number } | null;
}

export function resyncAgencyToGhl(
  agencyId: string,
): Promise<{ outcome: string; error: string | null; ghl: GhlAgencyStatus }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/ghl/resync`, { method: 'POST', body: {} });
}

/** "Keep out of GoHighLevel". The reason is required both ways, by the server too. */
export function setGhlKeepOut(
  agencyId: string,
  excluded: boolean,
  reason: string,
): Promise<{ ghl: GhlAgencyStatus }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/ghl/excluded`, {
    method: 'POST',
    body: { excluded, reason },
  });
}

export function fetchGhlSyncAll(): Promise<GhlSyncAllView> {
  return apiFetch('/api/admin/ghl/sync-all');
}

export function startGhlSyncAll(): Promise<GhlSyncAllView> {
  return apiFetch('/api/admin/ghl/sync-all', { method: 'POST', body: {} });
}

/* ---------------------------------------------------- Meta reporting (C-132) --- */

export interface MetaAgencyStatus {
  /** False when reporting is off on this server. */
  enabled: boolean;
  excluded: boolean;
}

export type MetaEventStatus = 'pending' | 'sent' | 'failed' | 'skipped' | 'excluded';

export interface MetaEvent {
  id: string;
  eventId: string;
  eventName: 'CompleteRegistration' | 'Purchase';
  agencyId: string;
  agencyName: string;
  paymentId: string | null;
  eventTime: string;
  status: MetaEventStatus;
  attempts: number;
  lastError: string | null;
  lastAttemptAt: string | null;
  sentAt: string | null;
  sentAsTest: boolean;
  fbtraceId: string | null;
}

export interface MetaPageView {
  enabled: boolean;
  offReason: string | null;
  datasetId: string | null;
  testEvents: boolean;
  graphVersion: string;
  counts: Record<MetaEventStatus, number>;
  events: MetaEvent[];
}

export function fetchMetaPage(): Promise<MetaPageView> {
  return apiFetch('/api/admin/meta');
}

/** "Keep out of Meta". The reason is required both ways, by the server too. */
export function setMetaKeepOut(
  agencyId: string,
  excluded: boolean,
  reason: string,
): Promise<{ meta: MetaAgencyStatus }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/meta/excluded`, {
    method: 'POST',
    body: { excluded, reason },
  });
}

/* ------------------------------------------------ activity (D-14 Item 4) --- */

export interface ActivityEvent {
  id: string;
  eventType: string;
  /**
   * The row as a sentence, **rendered by the server**.
   *
   * Not assembled here, for the same reason no money figure is: one place for
   * the wording, and it is inside the tree the em-dash guard scans. A browser
   * map of event type to sentence would be a second list to keep in step with
   * the first.
   */
  description: string;
  /** Null means the system acted, which is a real answer rather than missing. */
  actor: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface ActivityPage {
  events: ActivityEvent[];
  hasMore: boolean;
  /** Keyset cursor for the next page, or null at the end. */
  nextBefore: string | null;
}

export function fetchAgencyActivity(
  agencyId: string,
  before?: string | null,
): Promise<ActivityPage> {
  const query = before ? `?before=${encodeURIComponent(before)}` : '';
  return apiFetch(`/api/admin/agencies/${agencyId}/activity${query}`);
}

/* ---------------------------------------------- account states and cohort --- */

export function setAgencyStatus(
  agencyId: string,
  body: { status: AgencyStatus; reason: string; note?: string | null },
): Promise<{ agency: AdminAgencyDetail['agency'] }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/status`, { method: 'POST', body });
}

export function setAgencyCohort(
  agencyId: string,
  cohort: string | null,
): Promise<{ agency: AdminAgencyDetail['agency'] }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/cohort`, {
    method: 'PATCH',
    body: { cohort },
  });
}

/* ------------------------------------------------- invites (D-14 Item 3) --- */

export interface InviteAgencyInput {
  agencyName: string;
  ownerEmail: string;
  /** Typed twice and compared lowercased, here and again on the server. */
  ownerEmailConfirm: string;
  cohort?: string | null;
  credits?: number;
  creditsReason?: string | null;
}

export function inviteAgency(
  input: InviteAgencyInput,
): Promise<{ agency: AdminAgencyDetail['agency']; invite: AgencyInvite }> {
  return apiFetch('/api/admin/agencies/invite', { method: 'POST', body: input });
}

/**
 * A fresh link, and the old one dead.
 *
 * One call for resend and re-address, because they are one act with one extra
 * write. Passing an email re-addresses; passing none sends the same address a
 * new link.
 */
export function resendInvite(
  agencyId: string,
  email?: { email: string; emailConfirm: string },
): Promise<{ invites: AgencyInvite[]; agency: AdminAgencyDetail['agency'] | null }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/invite/resend`, {
    method: 'POST',
    body: email ?? {},
  });
}

/* --------------------------------------------- offboarding (D-14 Item 5) --- */

export interface OffboardingPayment {
  id: string;
  providerRef: string;
  createdAt: string;
  currency: string;
  creditsPurchased: number;
  amountMinor: number;
  taxMinor: number;
  totalMinor: number;
  /** (goods + tax) / credits, to the cent. The remainder rides the last credit. */
  unitMinor: number;
  unspentCredits: number;
  /** What those unspent credits are worth. Every figure the server's. */
  refundMinor: number;
  refundedMinor: number;
  refundState: 'none' | 'partial' | 'full';
  refundRef: string | null;
  refundedAt: string | null;
  disputedAt: string | null;
  disputeOutcome: string | null;
  /** Where to do it. Test-mode path while D-12 keeps the portal on test keys. */
  stripeUrl: string;
}

export interface OffboardingView {
  credits: CreditSummary;
  payments: OffboardingPayment[];
  totalRefundMinor: number;
  /** Credits no payment accounts for: granted, so there is nothing to give back. */
  unattributedCredits: number;
  currency: string;
  heldCredits: number;
}

export function fetchOffboarding(agencyId: string): Promise<OffboardingView> {
  return apiFetch(`/api/admin/agencies/${agencyId}/offboarding`);
}

export function zeroOffboardingBalance(
  agencyId: string,
  reason: string,
): Promise<{ credits: CreditSummary; removed: number }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/offboarding/zero-out`, {
    method: 'POST',
    body: { reason },
  });
}

export function recordRefundByHand(
  paymentId: string,
  body: { refundRef: string; reason: string; amountMinor?: number },
): Promise<{ payment: PaymentRecord }> {
  return apiFetch(`/api/admin/payments/${paymentId}/refund-by-hand`, {
    method: 'POST',
    body,
  });
}

export interface ReconciliationEvent {
  id: string;
  providerEventId: string;
  eventType: string;
  paymentId: string | null;
  agencyName: string | null;
  /** A chargeback with no payment behind it. Needs an operator, not a shrug. */
  isDispute: boolean;
  /** Provider references to search on, when the event carries them. */
  paymentIntentRef: string | null;
  chargeRef: string | null;
  createdAt: string;
  /** Set when an operator has put this aside as needing nothing (ruling A-3). */
  dismissedAt: string | null;
  dismissedReason: string | null;
  dismissedBy: string | null;
}

export interface ReconciliationPayment {
  id: string;
  agencyId: string;
  agencyName: string;
  providerRef: string;
  /** The goods price, ex-VAT (D-13). */
  amountMinor: number;
  taxMinor: number;
  /** What was actually charged: goods plus tax. */
  totalMinor: number;
  currency: string;
  creditsPurchased: number;
  status: 'initiated' | 'settled' | 'credited' | 'failed';
  packKey: string | null;
  creditedManually: boolean;
  /** The code that discounted this purchase, and by how much (D-8). */
  couponCode: string | null;
  discountMinor: number;
  failureReason: string | null;
  concern: 'settled_not_credited' | 'stale_initiated';
  createdAt: string;
}

/* ------------------------------------------------------------- coupons --- */

/**
 * The three ruled bands - D-9b ruling 8. **Computed by the server, never here.**
 *
 * The create form used to work its own out (`percent > 20`), which collapsed
 * 25% and 35% into one amber line, hid the bulk-deal band entirely and only
 * ever reached the loudest state when a code happened to be unbounded as well.
 * The server has computed and tested these all along.
 */
export interface CouponWarnings {
  /** Over 20% and up to 30%: deeper than the ladder, bulk-deal territory. */
  bulkDeal: boolean;
  /** Deeper than 30%. */
  subFloor: boolean;
  /** Either limit missing, so the giveaway has no ceiling. */
  unbounded: boolean;
  /** Once per account with no total-uses limit: bounded per account, not in total. */
  unboundedByAccounts: boolean;
  /** Deeper than 30%, or unbounded, or both. The loudest state. */
  severe: boolean;
}

export interface AdminCoupon {
  id: string;
  code: string;
  percentOff: number;
  expiresAt: string | null;
  maxRedemptions: number | null;
  maxCreditsPerRedemption: number | null;
  active: boolean;
  /** Each account may use it on one purchase - 29 September 2026. */
  oncePerAccount: boolean;
  note: string | null;
  /** Counted from settled payments, never stored. */
  redemptions: number;
  remainingRedemptions: number | null;
  /** Used past its maximum — tolerated by ruling, never silent (C-49). */
  overRedeemed: boolean;
  overRedeemedBy: number;
  /** Null means unbounded — either limit is missing. */
  worstCaseGiveawayMinor: number | null;
  /** One purchase at the credit cap. Null unless once per account with a cap. */
  worstCasePerAccountMinor: number | null;
  warnings: CouponWarnings;
  agencyIds: string[];
  createdAt: string;
}

/** What became of one payment against a code (C-114). The server decides. */
export type RedemptionState =
  | 'paid'
  | 'partly_refunded'
  | 'refunded'
  | 'replaced'
  | 'not_completed'
  | 'open'
  | 'disputed';

export interface CouponRedemption {
  paymentId: string;
  agencyId: string;
  agencyName: string;
  credits: number;
  discountMinor: number;
  chargedMinor: number;
  status: string;
  state: RedemptionState;
  createdAt: string;
}

export interface CreateCouponInput {
  code: string;
  percentOff: number;
  expiresAt?: string | null;
  maxRedemptions?: number | null;
  maxCreditsPerRedemption?: number | null;
  oncePerAccount?: boolean;
  note?: string | null;
  agencyIds?: string[];
}

export function fetchCoupons(): Promise<{ coupons: AdminCoupon[] }> {
  return apiFetch('/api/admin/coupons');
}

/** A coupon field the edit form can change - C-130. Everything but the code. */
export type CouponEditableField =
  | 'percentOff'
  | 'expiresAt'
  | 'maxRedemptions'
  | 'maxCreditsPerRedemption'
  | 'agencyIds'
  | 'oncePerAccount'
  | 'note'
  | 'active';

/** One entry in a code's history, newest first. Written by the server. */
export interface CouponHistoryEntry {
  id: string;
  action: string;
  actor: string;
  createdAt: string;
  details: {
    code?: string;
    changes?: { field: CouponEditableField; from: unknown; to: unknown }[];
  };
}

export function fetchCoupon(id: string): Promise<{
  coupon: AdminCoupon;
  redemptions: CouponRedemption[];
  /** C-130. Absent from a server older than it. */
  history?: CouponHistoryEntry[];
}> {
  return apiFetch(`/api/admin/coupons/${id}`);
}

/**
 * What the edit form sends - C-130. The same fields as create, with `active`
 * and without `code`: the code is fixed for life, and the server refuses a
 * request that tries to change it.
 */
export type UpdateCouponInput = Omit<CreateCouponInput, 'code'> & { active?: boolean };

export function updateCoupon(
  id: string,
  input: UpdateCouponInput,
): Promise<{ coupon: AdminCoupon }> {
  return apiFetch(`/api/admin/coupons/${id}`, { method: 'PATCH', body: input });
}

export interface CouponPreview {
  warnings: CouponWarnings;
  worstCaseGiveawayMinor: number | null;
  worstCasePerAccountMinor: number | null;
  ladderFloorPercent: number;
  bulkDealCeilingPercent: number;
}

/**
 * What a code would be, before it exists.
 *
 * The form asks rather than working it out. The three ruled bands and the
 * worst-case giveaway come from the same functions that judge a code once it
 * is real, so what an operator sees before and after cannot disagree.
 */
export function previewCoupon(input: {
  percentOff: number;
  maxRedemptions: number | null;
  maxCreditsPerRedemption: number | null;
  oncePerAccount?: boolean;
}): Promise<CouponPreview> {
  const query = new URLSearchParams({ percentOff: String(input.percentOff) });
  if (input.oncePerAccount === true) query.set('oncePerAccount', 'true');
  if (input.maxRedemptions !== null) query.set('maxRedemptions', String(input.maxRedemptions));
  if (input.maxCreditsPerRedemption !== null) {
    query.set('maxCreditsPerRedemption', String(input.maxCreditsPerRedemption));
  }
  return apiFetch(`/api/admin/coupons/preview?${query.toString()}`);
}

export function createCoupon(input: CreateCouponInput): Promise<{ coupon: AdminCoupon }> {
  return apiFetch('/api/admin/coupons', { method: 'POST', body: input });
}

export function setCouponActive(id: string, active: boolean): Promise<{ coupon: AdminCoupon }> {
  return apiFetch(`/api/admin/coupons/${id}/active`, { method: 'POST', body: { active } });
}

/**
 * Turns "once per account" on or off later. Switching it on refuses the code to
 * any account that has already used it.
 */
export function setCouponOncePerAccount(
  id: string,
  oncePerAccount: boolean,
): Promise<{ coupon: AdminCoupon }> {
  return apiFetch(`/api/admin/coupons/${id}/once-per-account`, {
    method: 'POST',
    body: { oncePerAccount },
  });
}

export interface ReconciliationCounts {
  unprocessedEvents: number;
  settledNotCredited: number;
  staleInitiated: number;
  dismissedEvents: number;
  /** Coupons used past their maximum — candidate C-49. */
  overRedeemedCoupons: number;
  /** Once-per-account codes one agency has paid with twice (29 September 2026). */
  oncePerAccountRepeats: number;
  /** D-14 Item 5. Three hints about offboarding and disputes, never blocks. */
  refundedNotZeroed: number;
  zeroedWithoutRefund: number;
  openDisputes: number;
}

/** Money went back and the credits stayed, or the other way round. */
export interface OffboardingConcern {
  agencyId: string;
  agencyName: string;
  status: AgencyStatus;
  availableCredits?: number;
  refundedAt?: string | null;
  refundRef?: string | null;
  refundedMinor?: number;
  zeroedAt?: string;
  /**
   * Where the zeroed credits came from - WAIT-A item 4. Only on
   * `zeroedWithoutRefund`, and `bought: 0` is what makes the row dismissible
   * without leaving the page.
   */
  removedCredits?: number;
  bought?: number;
  granted?: number;
}

export interface OpenDispute {
  paymentId: string;
  agencyId: string;
  agencyName: string;
  agencyStatus: AgencyStatus;
  providerRef: string;
  disputeRef: string | null;
  disputedAt: string;
  amountMinor: number;
  taxMinor: number;
  totalMinor: number;
  currency: string;
}

/** A code used more times than it was meant to be. */
export interface OverRedeemedCoupon {
  id: string;
  code: string;
  maxRedemptions: number;
  redemptions: number;
  overBy: number;
}

/** A once-per-account code that one agency got past the check with twice. */
export interface OncePerAccountRepeat {
  couponId: string;
  code: string;
  agencyId: string;
  agencyName: string;
  uses: number;
}

export interface ReconciliationView {
  counts: ReconciliationCounts;
  showingDismissed: boolean;
  unprocessedEvents: ReconciliationEvent[];
  payments: ReconciliationPayment[];
  overRedeemedCoupons: OverRedeemedCoupon[];
  oncePerAccountRepeats: OncePerAccountRepeat[];
  recentActions: AdminAction[];
  /** D-14 Item 5. Three lists, each a pair of acts that should have gone together. */
  refundedNotZeroed: OffboardingConcern[];
  zeroedWithoutRefund: OffboardingConcern[];
  openDisputes: OpenDispute[];
}

/* ------------------------------------------------------- sales (D-13) --- */

/** The named periods the server knows how to work out. */
export const SALES_PERIODS = [
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'this_year',
  'all',
] as const;

export type SalesPeriodKey = (typeof SALES_PERIODS)[number];

/**
 * One payment on the sales view.
 *
 * Every money figure is the server's, including the total: the browser does no
 * arithmetic on money anywhere in this portal, and a VAT return is not the
 * place to start.
 */
export interface SalesPayment {
  id: string;
  createdAt: string;
  agencyId: string;
  agencyName: string;
  credits: number;
  /** The goods price, ex-VAT. */
  amountMinor: number;
  taxMinor: number;
  totalMinor: number;
  currency: string;
  status: 'initiated' | 'settled' | 'credited' | 'failed';
  packKey: string | null;
  couponCode: string | null;
  discountMinor: number;
  creditedManually: boolean;
  failureReason: string | null;
}

/** The three figures that are the VAT return, for one currency. */
export interface SalesTotals {
  currency: string;
  sales: number;
  goodsMinor: number;
  taxMinor: number;
  totalMinor: number;
}

export interface SalesView {
  /** Which period these figures cover. `from`/`to` are null for all time. */
  period: { key: SalesPeriodKey | 'custom'; from: string | null; to: string | null };
  /** The statuses the totals count, named by the server rather than assumed. */
  countedStatuses: string[];
  currency: string;
  totals: SalesTotals[];
  sales: SalesPayment[];
  salesTruncated: boolean;
  /** Checkouts that took no money. Never hidden, never counted as sales. */
  unpaid: SalesPayment[];
  unpaidTruncated: boolean;
  unpaidCounts: { abandoned: number; failed: number };
  rowLimit: number;
}

/** A custom range, as two inclusive days. Both or neither. */
export interface SalesRange {
  from: string;
  to: string;
}

export function fetchAdminSales(
  selection: { period: SalesPeriodKey } | SalesRange,
): Promise<SalesView> {
  const params = new URLSearchParams(
    'period' in selection
      ? { period: selection.period }
      : { from: selection.from, to: selection.to },
  );
  return apiFetch(`/api/admin/sales?${params.toString()}`);
}

export interface RenewalSite {
  siteId: string;
  agencyId: string;
  agencyName: string;
  businessName: string;
  liveUrl: string | null;
  liveUntil: string;
  daysRemaining: number;
}

/* ------------------------------------------------------------------ calls --- */

/** What the queue is narrowed to. `closed` is the D-6 terminal-states group. */
export type QueueFilter = { status?: OrderStatus; group?: 'closed' };

export function fetchAdminOrders(
  filter: QueueFilter = {},
): Promise<{ orders: AdminOrderListItem[] }> {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  else if (filter.group) params.set('group', filter.group);
  const query = params.toString();
  return apiFetch(`/api/admin/orders${query ? `?${query}` : ''}`);
}

export interface OperatorSite {
  siteId: string;
  agencyId: string;
  agencyName: string;
  businessName: string;
  liveUrl: string | null;
  goLiveDate: string | null;
  liveUntil: string | null;
  daysRemaining: number | null;
}

/** Every site in the portal, whatever its horizon — the C-7 "All sites" view. */
export function fetchAdminSites(): Promise<{ sites: OperatorSite[] }> {
  return apiFetch('/api/admin/sites');
}

export function fetchAdminOrder(id: string): Promise<AdminOrderDetail> {
  return apiFetch(`/api/admin/orders/${id}`);
}

export function transitionOrder(
  id: string,
  body: {
    to: OrderStatus;
    note?: string;
    /**
     * D-14 Item 9.2. `liveUrl` and `linkUrls` are required by the server on a
     * build going live, and optional in this type because a renewal sends no
     * `site` block at all.
     */
    site?: {
      liveUrl?: string;
      customDomain?: string;
      goLiveDate?: string;
      linkUrls?: string[];
    };
  },
): Promise<{ order: Order; siteId: string | null; timeline: TimelineEvent[] }> {
  return apiFetch(`/api/admin/orders/${id}/transitions`, { method: 'POST', body });
}

export function fetchAdminAgencies(): Promise<{
  agencies: AdminAgencyListItem[];
  /** The window the lifecycles were read against, so the page says one number. */
  lifecycleWindowDays: number;
  currency: string;
}> {
  return apiFetch('/api/admin/agencies');
}

/**
 * Where the export lives - D-14 Item 6.
 *
 * A plain link rather than a fetch: the browser's own download handling is
 * what puts the file somewhere the operator can find it, and a blob built in
 * JavaScript would lose the filename the server sets.
 */
export const ADMIN_AGENCIES_CSV_URL = `${API_URL}/api/admin/agencies.csv`;

export function fetchAdminAgency(id: string): Promise<AdminAgencyDetail> {
  return apiFetch(`/api/admin/agencies/${id}`);
}

export function adjustCredits(
  agencyId: string,
  body: { amount: number; reason: string },
): Promise<{ credits: CreditSummary; ledger: { entries: LedgerEntry[] } }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/adjustments`, { method: 'POST', body });
}

export function fetchReconciliation(dismissed = false): Promise<ReconciliationView> {
  return apiFetch(`/api/admin/reconciliation${dismissed ? '?dismissed=true' : ''}`);
}

/** Sets an event aside as needing nothing. Reversible; both halves are audited. */
export function dismissEvent(
  eventId: string,
  reason: string,
): Promise<{ dismissed: boolean; counts: ReconciliationCounts }> {
  return apiFetch(`/api/admin/reconciliation/events/${eventId}/dismiss`, {
    method: 'POST',
    body: { reason },
  });
}

export function restoreEvent(
  eventId: string,
  reason: string,
): Promise<{ dismissed: boolean; counts: ReconciliationCounts }> {
  return apiFetch(`/api/admin/reconciliation/events/${eventId}/restore`, {
    method: 'POST',
    body: { reason },
  });
}

export function creditPaymentManually(
  paymentId: string,
  reason: string,
): Promise<{ payment: PaymentRecord; creditedManually: boolean }> {
  return apiFetch(`/api/admin/payments/${paymentId}/credit`, {
    method: 'POST',
    body: { reason },
  });
}

export function failPayment(
  paymentId: string,
  reason: string,
): Promise<{ payment: PaymentRecord }> {
  return apiFetch(`/api/admin/payments/${paymentId}/fail`, { method: 'POST', body: { reason } });
}

export function fetchAdminRenewals(withinDays = 45): Promise<{
  withinDays: number;
  sites: RenewalSite[];
}> {
  return apiFetch(`/api/admin/renewals?withinDays=${withinDays}`);
}

/**
 * Uploads a deliverable.
 *
 * Uses `fetch` directly rather than `apiFetch` because the body is multipart:
 * the browser must set its own Content-Type with the boundary, so no header may
 * be forced here.
 */
export async function uploadDeliverable(
  orderId: string,
  input: { file: File; kind: string; label: string },
): Promise<{ deliverables: Deliverable[] }> {
  const form = new FormData();
  form.append('kind', input.kind);
  form.append('label', input.label);
  form.append('file', input.file);

  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/deliverables`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const body = payload as { error?: { code?: string; message?: string; details?: unknown } };
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'internal_error',
      body?.error?.message ?? `Upload failed (${response.status}).`,
      body?.error?.details,
    );
  }

  return payload as { deliverables: Deliverable[] };
}

/* ---------------------------------------------------------- pricing (D-9) --- */

export interface AdminOrderCost {
  id: string;
  orderType: 'site_build' | 'year_extension' | 'content_refresh';
  credits: number;
  updatedAt: string;
}

/** One rung of the ladder, as the operator edits it. */
export interface AdminDiscountTier {
  id: string;
  buildsThreshold: number;
  numerator: number;
  denominator: number;
  updatedAt: string;
}

/** A rung, as it comes out at a given build cost. */
export interface AdminLadderRung {
  minBuilds: number | null;
  minCredits: number;
  /** What a build costs at this rung. The operator's unit (D-9b ruling 13). */
  perBuildAmountMinor: number;
  ratePerCreditMinor: number;
  discountPercent: number;
  thresholdAmountMinor: number;
}

/** A pack as the Pricing page sees it, every figure from the server. */
export interface AdminPricingPack {
  key: string;
  name: string;
  builds: number;
  credits: number;
  amountMinor: number;
  perBuildAmountMinor: number;
  savingMinor: number;
  discountPercent: number;
}

/** What the ladder and the packs come to at one build cost. */
export interface AdminPricingSnapshot {
  buildCredits: number;
  /** What a build costs with no discount, the anchor every rung is read against. */
  rackPerBuildAmountMinor: number;
  tiers: AdminLadderRung[];
  packs: AdminPricingPack[];
  /** Packs that would pass the single-order maximum at this cost. */
  unsellablePacks: { key: string; name: string; credits: number }[];
}

export interface AdminPricingResponse {
  orderCosts: AdminOrderCost[];
  ladder: AdminDiscountTier[];
  current: AdminPricingSnapshot;
  /** Every change to a cost or the ladder, newest first, from the audit trail. */
  history: AdminAction[];
}

export interface AdminPricingPreview {
  current: AdminPricingSnapshot;
  proposed: AdminPricingSnapshot;
}

export function previewOrderCost(credits: number): Promise<AdminPricingPreview> {
  return apiFetch(`/api/admin/order-costs/preview?credits=${String(credits)}`);
}

/**
 * Saves the ladder in **dollars a build** (D-9b ruling 13).
 *
 * The server derives the exact fraction and stores it exactly as before, so
 * the browser never does the rounding that decides a price.
 */
export function replaceDiscountLadder(
  tiers: { buildsThreshold: number; perBuildAmountMinor: number }[],
  reason: string | null,
): Promise<{ ladder: AdminDiscountTier[]; current: AdminPricingSnapshot }> {
  return apiFetch('/api/admin/discount-ladder', { method: 'PUT', body: { tiers, reason } });
}

export function fetchOrderCosts(): Promise<AdminPricingResponse> {
  return apiFetch('/api/admin/order-costs');
}

export function setOrderCost(
  orderType: string,
  credits: number,
  reason: string | null,
): Promise<{ orderCost: AdminOrderCost }> {
  return apiFetch(`/api/admin/order-costs/${orderType}`, {
    method: 'PUT',
    body: { credits, reason },
  });
}

/* ------------------------------------------------ email templates (Item 3) --- */

/**
 * One template as the Emails page edits it.
 *
 * **Every rule here is the server's.** `allowedPlaceholders`,
 * `requiredPlaceholders` and `toggleable` come down with the copy rather than
 * being restated in the browser, so what an operator is warned about before
 * saving and what decides whether the save lands cannot disagree. The page
 * checks the same things the API does; the API is what actually refuses.
 */
export interface AdminEmailTemplate {
  key: string;
  label: string;
  /** One line saying when this email goes out. */
  when: string;
  audience: 'customer' | 'operator';
  /** False only for the verification email, which has no switch at all. */
  toggleable: boolean;
  allowedPlaceholders: string[];
  /** Placeholders a save may not remove. */
  requiredPlaceholders: string[];
  subject: string;
  body: string;
  enabled: boolean;
  /** False when no row exists and the built-in copy is being shown instead. */
  stored: boolean;
  updatedAt: string | null;
  /** Where it actually goes, in words - WAIT-D16 polish 7. */
  recipient: string;
  /** Why it has no switch, when it has none - WAIT-D16 polish 6. */
  lockedReason: string | null;
  /** True when the code appends a tax block under the operator's words. */
  hasCodeOwnedTaxBlock: boolean;
}

export interface AdminEmailTemplates {
  templates: AdminEmailTemplate[];
  /** Where an operator template goes, named rather than described as "us". */
  operatorAddress: string;
  /**
   * Whether anything could actually be delivered. False on the console driver,
   * which writes to a log rather than sending. Said plainly, because an
   * operator who presses "send test" and sees nothing arrive deserves to have
   * been told why beforehand.
   */
  notificationsConfigured: boolean;
  /** Every save and toggle, newest first, from the shared audit trail. */
  history: AdminAction[];
}

export function fetchEmailTemplates(): Promise<AdminEmailTemplates> {
  return apiFetch('/api/admin/email-templates');
}

export function saveEmailTemplate(
  key: string,
  copy: { subject: string; body: string },
  reason: string | null,
): Promise<{ template: AdminEmailTemplate }> {
  return apiFetch(`/api/admin/email-templates/${key}`, {
    method: 'PUT',
    body: { ...copy, reason },
  });
}

/** One call both ways, so the direction that undoes the other cannot be forgotten. */
export function setEmailTemplateEnabled(
  key: string,
  enabled: boolean,
): Promise<{ template: AdminEmailTemplate }> {
  return apiFetch(`/api/admin/email-templates/${key}/enabled`, {
    method: 'POST',
    body: { enabled },
  });
}

export interface TestSendResult {
  outcome: 'sent' | 'disabled' | 'failed';
  to: string;
  /** True when the template is switched off and was sent anyway. */
  wasDisabled: boolean;
}

/** Sends one template, with sample values, even when it is switched off. */
export function sendTestEmail(key: string, to: string): Promise<TestSendResult> {
  return apiFetch(`/api/admin/email-templates/${key}/test-send`, {
    method: 'POST',
    body: { to },
  });
}

/* ------------------------------------------------- contact inbox (D-9b 16) --- */

export interface ContactMessage {
  id: string;
  agencyId: string;
  agencyName: string;
  userEmail: string;
  category: 'volume_order' | 'support' | 'feedback';
  fields: Record<string, string>;
  body: string;
  status: 'new' | 'read';
  readAt: string | null;
  /** When it was set aside. Null means it is in the Inbox. Never a delete. */
  archivedAt: string | null;
  createdAt: string;
}

export interface ContactInbox {
  messages: ContactMessage[];
  /** Which list came back, echoed so the page cannot render the wrong tab. */
  archived: boolean;
  unread: number;
  archivedCount: number;
  /**
   * Whether the portal can notify the operator at all.
   *
   * Shown plainly, because an operator who thinks they will be emailed and is
   * not would find messages days late. "Nothing has arrived" and "nothing has
   * told me anything arrived" are different states.
   */
  notificationsConfigured: boolean;
  notifyAddress: string;
}

export function fetchContactInbox(archived = false): Promise<ContactInbox> {
  return apiFetch(`/api/admin/contact-messages${archived ? '?archived=true' : ''}`);
}

/**
 * Sets a message aside, or brings it back - ruling 25.
 *
 * One call both ways, because the two directions are one timestamp and an
 * archive you cannot undo is a delete wearing a softer word.
 */
export function setContactMessageArchived(
  id: string,
  archived: boolean,
): Promise<{ message: ContactMessage }> {
  return apiFetch(`/api/admin/contact-messages/${id}/archived`, {
    method: 'POST',
    body: { archived },
  });
}

/** Opening a message is the act of reading it, so this marks it read. */
export function openContactMessage(id: string): Promise<{ message: ContactMessage }> {
  return apiFetch(`/api/admin/contact-messages/${id}`);
}

/* ------------------------------------------- view as agency (Item 7) --- */

/**
 * Starts looking at an agency as its owner sees it - D-14 Item 7.
 *
 * **Issues a cookie, not a session.** Nothing in the browser holds the marker:
 * it is `httpOnly`, so the page learns it is in a view-as only from
 * `/api/me`'s `viewingAs`, which is refreshed after this resolves.
 */
export function startViewAs(
  agencyId: string,
): Promise<{ agencyName: string; expiresAt: string }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/view-as`, { method: 'POST', body: {} });
}

/** Stops. Works whatever state the cookie is in, which is what Exit must do. */
export function stopViewAs(): Promise<{ viewing: boolean }> {
  return apiFetch('/api/admin/view-as', { method: 'DELETE' });
}


/* ------------------------------------------ the handover edit (Item 9.4) --- */

/**
 * Corrects a handover after go-live - D-14 Item 9.4, **built at WAIT-B**.
 *
 * The route has existed since Phase B and nothing called it: `checkHandover`
 * validated, `site.handover` audited, and no operator screen offered it (WAIT-B
 * defect 12). A route nobody can reach is a route that is not built.
 */
export function updateSiteHandover(
  siteId: string,
  body: { liveUrl: string; linkUrls: string[] },
): Promise<{ site: Site }> {
  return apiFetch(`/api/admin/sites/${siteId}/handover`, { method: 'PATCH', body });
}


/* ------------------------------------------------- D-16, the tax number --- */

/** Asks VIES again about whatever number the account has - item 10. */
export function recheckTaxId(agencyId: string): Promise<{ agency: AdminAgencyDetail['agency'] }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/tax-check`, { method: 'POST' });
}

/**
 * Records that the operator confirmed a number themselves - item 10.
 *
 * **The note is required by the server**, and that is the point: this is the
 * only way an account reaches `valid` without VIES having said so, so the audit
 * row has to say where the operator checked.
 */
export function markTaxIdCheckedByHand(
  agencyId: string,
  note: string,
): Promise<{ agency: AdminAgencyDetail['agency'] }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/tax-check/by-hand`, {
    method: 'POST',
    body: { note },
  });
}

/* --------------------------------------- C-37, the owner's login address --- */

/**
 * Marks the owner's login address confirmed, for an owner the confirmation
 * email never reached - C-37.
 *
 * **The reason is required by the server**, on the "Mark checked by hand"
 * pattern: it makes a claim true by assertion, so the audit row has to say how
 * the operator knows the address is theirs. Refused for a closed account and
 * for an address already confirmed.
 */
export function markOwnerEmailConfirmed(
  agencyId: string,
  reason: string,
): Promise<{ owner: { email: string; emailVerified: boolean } }> {
  return apiFetch(`/api/admin/agencies/${agencyId}/owner-email/confirm`, {
    method: 'POST',
    body: { reason },
  });
}
